/**
 * @fileoverview Component for adding a new external ID.
 * This component includes a form for adding a new external ID and a button for confirming the addition.
 */

import { XMarkIcon } from '@heroicons/react/24/solid'
import { useState } from 'react'
import { Tooltip } from 'react-tooltip'
import QuestionIcon from '../../../assets/question.svg'
import { useModal } from '../../../hooks/useModal'
import { PrimaryButton } from '../../elements/PrimaryButton'
import { RegularButton } from '../../elements/RegularButton'

/**
 * @interface ConfirmNewTestProps
 * @description Props for the AddNewExternalID component.
 * @property {function} onConfirm - The function to call when the form is confirmed.
 */
interface ConfirmNewTestProps {
    onConfirm: (form: Record<string, string>) => Promise<unknown>
}

/**
 * @function AddNewExternalID
 * @description Component for adding a new external ID.
 * @param {ConfirmNewTestProps} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the add new external ID form.
 */
export function AddNewExternalID({ onConfirm }: ConfirmNewTestProps) {
    const [, close] = useModal()
    const [form, setForm] = useState({
        name: '',
        external_id: '',
    })
    return (
        <div className="w-[26rem]">
            <div className="flex justify-between gap-4 mb-6">
                <div className="flex">
                    <h2 className="block font-semibold text-xl text-gray-900">Add External ID</h2>
                </div>
                <button className="flex shrink-0 w-6 h-6" onClick={close}>
                    <XMarkIcon className="w-6 h-6" />
                </button>
            </div>
            <div className="">
                <div>
                    <div className="mb-1">
                        <label htmlFor="name" className="flex text-sm font-medium text-gray-700">
                            <span>External ID</span>
                            <div className="relative flex justify-end grow">
                                <QuestionIcon
                                    id={`call-tooltip`}
                                    className="w-5 h-5 fill-gray-400 transition-colors cursor-pointer hover:fill-indigo-500"
                                />
                                <Tooltip anchorSelect={`#call-tooltip`}>
                                    <p>Add external ID</p>
                                </Tooltip>
                            </div>
                        </label>
                    </div>
                    <div className="flex">
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={form.name}
                            placeholder="Name"
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="h-10 w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm border-gray-300 rounded-l-md"
                        />
                        <input
                            type="text"
                            id="external_id"
                            name="external_id"
                            value={form.external_id}
                            required
                            placeholder="ID"
                            onChange={(e) => setForm({ ...form, external_id: e.target.value })}
                            className={`h-10 -ml-[1px] w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm border-gray-300 rounded-r-md`}
                        />
                    </div>
                </div>
            </div>

            <section className="flex flex-row gap-2 mt-8">
                <RegularButton className="flex grow shrink-0 basis-0" onClick={close}>
                    Cancel
                </RegularButton>
                <PrimaryButton
                    className="flex grow shrink-0 basis-0"
                    onClick={async () => {
                        await onConfirm(form)
                        close()
                    }}
                >
                    Add External ID
                </PrimaryButton>
            </section>
        </div>
    )
}
